import { Link } from "react-router-dom";
import { ArrowRight, Mic, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

export default function Hero() {
  return (
    <section
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Soft ambient gradient blobs */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-[#D1FAE5] blur-3xl opacity-60" />
      <div className="pointer-events-none absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#ECFDF5] blur-3xl opacity-80" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left column */}
        <div className="lg:col-span-7 fade-up">
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#D1FAE5] text-[#065F46] text-xs font-semibold uppercase tracking-[0.18em] shadow-soft mb-7"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Built for therapy & rehab clinicians
          </div>

          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] leading-[1.05] text-balance">
            Document less.{" "}
            <span className="text-[#059669]">Treat more.</span>
          </h1>

          <p className="font-figtree mt-6 text-base sm:text-lg text-[#475569] leading-relaxed max-w-2xl">
            Speak naturally about a session. TherapyNote routes each spoken
            section into the right clinical field — tuned for your discipline.
            Review, save, done.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" data-testid="hero-trial-btn">
              <Button
                size="lg"
                className="w-full sm:w-auto rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-base font-medium px-7 py-6 shadow-[0_10px_25px_rgba(16,185,129,0.35)] transition-all duration-300 hover:-translate-y-0.5"
              >
                Start 15-day free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/demo" data-testid="hero-demo-btn">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto rounded-full bg-white hover:bg-slate-50 border-slate-200 text-[#0F172A] font-figtree text-base font-medium px-7 py-6 shadow-soft transition-all duration-300 hover:-translate-y-0.5"
              >
                <Mic className="w-4 h-4 mr-2 text-[#059669]" />
                Try the live demo
              </Button>
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#475569] font-figtree">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              No credit card required
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              Works in Chrome & Edge
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              Privacy-first by design
            </span>
          </div>
        </div>

        {/* Right column - visual */}
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(15,23,42,0.12)] border border-white">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/d066d172-2191-4a64-b7f6-08cdd9cfdc8c/images/ca09964ec64ef66609a4f3ff39d7afe41285662ba004f75523e7f67d2bb27d58.png"
              alt="AI transforming soundwaves into clinical notes"
              className="w-full h-[420px] md:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#064E3B]/30 via-transparent to-transparent" />
          </div>

          {/* Floating mic card */}
          <div className="absolute -bottom-6 -left-6 sm:-left-10 bg-white rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.10)] p-4 pr-6 flex items-center gap-4 border border-slate-100">
            <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#10B981] text-white mic-pulse">
              <Mic className="w-5 h-5" />
            </span>
            <div>
              <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                Listening...
              </div>
              <div className="font-figtree text-xs text-[#475569]">
                "Patient reports decreased pain..."
              </div>
            </div>
          </div>

          {/* Floating fields card */}
          <div className="hidden sm:block absolute -top-6 -right-4 bg-white rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.10)] p-4 border border-slate-100 w-56">
            <div className="font-figtree text-[10px] uppercase tracking-[0.18em] text-[#059669] font-semibold mb-1.5">
              Auto-filled
            </div>
            <div className="space-y-1.5">
              <div className="h-2 rounded-full bg-slate-100 w-full" />
              <div className="h-2 rounded-full bg-slate-100 w-4/5" />
              <div className="h-2 rounded-full bg-[#D1FAE5] w-3/5" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
